import React, { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import { FormControl } from "react-bootstrap";
import { Dropdown } from "react-bootstrap";
import DatePickerComponent from "src/components/datePicker";
import Tooltip from 'src/components/tooltip';
import dateFormat from "dateformat";

import {
  daysDifference,
  getReportDatesForOption,
} from "src/services/reports.service";
import { ALL_TIME, CUSTOM, DATE_RANGES, TODAY } from "src/constants/reports";

import _ from "lodash";
import cx from "classnames";
import styles from "./dateFilter.module.scss";

interface ReportDatePickerProps {
  dateRanges: typeof DATE_RANGES;
  value: {
    startDate: Moment;
    endDate: Moment;
    type: string;
  };
  filter: {
    interval?: string;
  };
  onChange: (value: {
    reportDate: {
      type: string;
      startDate: Moment;
      endDate: Moment;
    };
  }) => void;
  handleTimeLineChange: (selectedTimeline: string) => void;
  className?: string;
  showTimelineFilter?: boolean;
}

export const ReportDatePicker: React.FC<ReportDatePickerProps> = ({
  dateRanges,
  value,
  filter,
  onChange,
  handleTimeLineChange,
  className,
  showTimelineFilter = true,
}) => {
  const id = `report-date-picker-${Date.now()}`;
  const [open, setOpen] = useState<boolean>(false);

  const [startDate, setStartDate] = useState<Moment>(value.startDate);
  const [endDate, setEndDate] = useState<Moment>(value.endDate);
  const [activeTimelineFilter, setActiveTimelineFilter] = useState<string>(
    _.get(filter, "interval", "day"),
  );
  const daysBetween = daysDifference(startDate, endDate);

  const handleToggleDropdown = (isOpen: boolean) => {
    setOpen(isOpen);
    setStartDate(value.startDate);
    setEndDate(value.endDate);
  };

  const handleOptionSelection = (key: string) => {
    const dates = getReportDatesForOption(key);
    onChange({
      reportDate: {
        type: key,
        startDate: dates.startDate,
        endDate: dates.endDate,
      },
    });
    setOpen(false);
  };

  const handleCustomDateChange = () => {
    onChange({
      reportDate: {
        type: CUSTOM,
        startDate: startDate,
        endDate: endDate,
      },
    });
    setOpen(false);
    setStartDate(moment(_.get(value, "startDate", startDate)));
    setEndDate(moment(_.get(value, "endDate", endDate)));
  };

  const renderLabel = (): string => {
    const type = _.get(value, "type") || TODAY;
    if (type === CUSTOM) {
      const startDate = _.get(value, "startDate") || moment();
      const endDate = _.get(value, "endDate") || moment();

      return `${dateFormat(startDate.toDate(), "dd/mm/yyyy")} - ${dateFormat(endDate.toDate(), "dd/mm/yyyy")}`;
    }
    const option = DATE_RANGES.find((item) => item.label === type);
    return option ? option.value : "";
  };

  useEffect(() => {
    setStartDate(value.startDate);
    setEndDate(value.endDate);
  }, [value.startDate, value.endDate]);

  useEffect(() => {
    setActiveTimelineFilter(_.get(filter, "interval", ""));
  }, [filter.interval]);

  return (
    <Dropdown
      id={id}
      className={cx(styles.datepicker, className)}
      show={open}
      onToggle={handleToggleDropdown}
    >
      <Dropdown.Toggle className={styles.action}>
        <div className={styles.buttonContent}>
          {renderLabel()}
          <span className={styles.arrow}>
            <i className="paperflite-dropdown" />
          </span>
        </div>
      </Dropdown.Toggle>
      {_.size(dateRanges) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          <div className={styles.options}>
            {dateRanges.map((item) => {
              const active = _.get(value, "type") === item.label;
              return (
                <div
                  key={item.label}
                  className={cx(styles.option, { [styles.active]: active })}
                  onClick={() => handleOptionSelection(item.label)}
                >
                  {item.label === ALL_TIME && (
                    <span className={styles.infoLabel}>
                      {item.value}
                      <Tooltip
                        content="Represents data from the past 2 years up to today"
                        placement="right"
                      >
                        <i className={`paperflite-info ${styles.tooltipIcon}`} />
                      </Tooltip>
                    </span>
                  )}

                  {item.label !== ALL_TIME &&
                    item.value
                  }
                </div>
              );
            })}
          </div>
          {showTimelineFilter && <div className={styles.timelineFilter}>
            <div
              className={cx(styles.filter, {
                [styles.active]: activeTimelineFilter === "day",
                [styles.disabled]: daysBetween > 365,
              })}
              onClick={() => {
                if (daysBetween < 365) {
                  setActiveTimelineFilter("day");
                  handleTimeLineChange("day");
                  setOpen(false);
                }
              }}
            >
              <i
                className={cx(
                  activeTimelineFilter === "day"
                    ? "paperflite-radio-button-selected"
                    : "paperflite-radio-button-unselected",
                )}
                aria-hidden="true"
              />
              <span>Day</span>
            </div>
            <div
              className={cx(styles.filter, {
                [styles.active]: activeTimelineFilter === "week",
                [styles.disabled]: daysBetween < 14,
              })}
              onClick={() => {
                if (daysBetween >= 14) {
                  setActiveTimelineFilter("week");
                  handleTimeLineChange("week");
                  setOpen(false);
                }
              }}
            >
              <i
                className={cx(
                  activeTimelineFilter === "week"
                    ? "paperflite-radio-button-selected"
                    : "paperflite-radio-button-unselected",
                )}
                aria-hidden="true"
              />
              <span>Week</span>
            </div>
            <div
              className={cx(styles.filter, {
                [styles.active]: activeTimelineFilter === "month",
                [styles.disabled]: daysBetween < 60,
              })}
              onClick={() => {
                if (daysBetween >= 60) {
                  setActiveTimelineFilter("month");
                  handleTimeLineChange("month");
                  setOpen(false);
                }
              }}
            >
              <i
                className={cx(
                  activeTimelineFilter === "month"
                    ? "paperflite-radio-button-selected"
                    : "paperflite-radio-button-unselected",
                )}
                aria-hidden="true"
              />
              <span>Month</span>
            </div>
          </div>}
          <div className={styles.form}>
            <div className={cx(styles.date, styles.start)}>
              <label>Start Date:</label>
              <DateInput
                key={startDate.toString()}
                value={startDate}
                onChange={(newVal) => {
                  if (!newVal) return;
                  setStartDate(newVal);
                  setEndDate(moment.max(newVal, endDate));
                }}
              />
            </div>
            <div className={cx(styles.date, styles.end)}>
              <label>End Date:</label>
              <DateInput
                key={endDate.toString()}
                value={endDate}
                onChange={(newVal) => {
                  if (!newVal) return;
                  setStartDate(moment.min(newVal, startDate));
                  setEndDate(newVal);
                }}
              />
            </div>
            <button className={styles.submit} onClick={handleCustomDateChange}>
              Apply
            </button>
          </div>
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

interface DateInputProps {
  value: Moment;
  onChange: (value: Moment) => void;
}

export const DateInput: React.FC<DateInputProps> = ({ value, onChange }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? value.toDate() : null,
  );

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    onChange(moment(date));
  };

  return (
    <DatePickerComponent
      customInput={<CustomDateInput />}
      value={selectedDate}
      handleDateChange={handleDateChange}
      maxDate={moment().add(5, "year").toDate()}
      minDate={moment().subtract(5, "year").toDate()}
    />
  );
};

export const CustomDateInput = React.forwardRef<
  HTMLInputElement,
  { value?: string; onClick?: () => void }
>((props, ref) => (
  <FormControl
    type="text"
    value={props.value || ""}
    onClick={props.onClick}
    ref={ref}
    readOnly
  />
));

CustomDateInput.displayName = "CustomDateInput";

export default ReportDatePicker;
